import React from "react";
import { Link } from "react-router-dom";
import { Users, ClipboardList, ArrowRight, ShieldCheck, Clock, CheckCircle2 } from "lucide-react";
import { useSEO, breadcrumbSchema, SITE_URL } from "../lib/seo";
import { svgContentTileUrl } from "../data/propertyPlaceholders";

export default function ReferralsPage() {
  useSEO({
    title: "Make a Referral | NDIS Provider Melbourne & Perth | Alpha Community Care",
    description: "Refer to Alpha Community Care — quick participant / family form or a compliant coordinator form. NDIS-registered, acknowledged within 2 business hours across Melbourne, Perth and nationally.",
    keywords: ["NDIS referral form", "refer participant Melbourne", "refer participant Perth", "coordinator referral NDIS", "SIL referral", "SDA referral"],
    canonical: `${SITE_URL}/referrals`,
    jsonLd: [breadcrumbSchema([{ name: "Home", path: "/" }, { name: "Referrals", path: "/referrals" }])],
  });
  return (
    <div data-testid="page-referrals">
      <section className="alpha-gradient-hero border-b border-[color:var(--alpha-line)]">
        <div className="alpha-container pt-14 pb-10">
          <div className="alpha-overline">Referrals</div>
          <h1 className="mt-3 text-4xl sm:text-5xl font-display font-semibold tracking-tight">
            Refer in minutes. Move forward with confidence.
          </h1>
          <p className="mt-4 max-w-2xl text-[color:var(--alpha-mute)]">
            Two clear paths — whether you're a participant or family, or a coordinator / provider making a formal referral.
          </p>
        </div>
      </section>

      <section className="alpha-section">
        <div className="alpha-container grid md:grid-cols-2 gap-6">
          {[
            {
              to: "/referrals/participant",
              testid: "card-participant-referral",
              Icon: Users,
              tag: "For participants & families",
              title: "I need support or a home for myself or a loved one",
              text: "Quick, plain-English form. Tell us what you need and we'll match you with the right services — no NDIS jargon required.",
              img: svgContentTileUrl("referral-participant", "For Participants & Families"),
              cta: "Start participant referral",
            },
            {
              to: "/referrals/coordinator",
              testid: "card-coordinator-referral",
              Icon: ClipboardList,
              tag: "For coordinators & providers",
              title: "I'm making a referral for a participant",
              text: "A pro-tier intake form built for speed, compliance and clarity. Acknowledged within 2 business hours.",
              img: svgContentTileUrl("referral-coordinator", "For Coordinators & Providers"),
              cta: "Start coordinator referral",
            },
          ].map((c) => {
            const Icon = c.Icon;
            return (
              <Link key={c.to} to={c.to} className="group relative overflow-hidden rounded-3xl border border-[color:var(--alpha-line)] bg-white alpha-focus" data-testid={c.testid}>
                <div className="relative h-56 overflow-hidden">
                  <img src={c.img} alt={c.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" loading="lazy" />
                  <div className="absolute inset-0 bg-gradient-to-t from-white/95 via-white/30 to-transparent" />
                </div>
                <div className="relative -mt-14 p-7">
                  <div className="w-12 h-12 rounded-xl alpha-gradient text-white flex items-center justify-center shadow-lg">
                    <Icon className="w-6 h-6" />
                  </div>
                  <div className="alpha-overline mt-4">{c.tag}</div>
                  <h3 className="mt-2 font-display text-2xl font-semibold leading-tight">{c.title}</h3>
                  <p className="mt-3 text-sm text-[color:var(--alpha-mute)]">{c.text}</p>
                  <div className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-[color:var(--alpha-purple-700)] group-hover:gap-3 transition-all">
                    {c.cta} <ArrowRight className="w-4 h-4" />
                  </div>
                </div>
              </Link>
            );
          })}
        </div>

        <div className="alpha-container mt-12 grid md:grid-cols-3 gap-5">
          {[
            { Icon: ShieldCheck, title: "Fully compliant", text: "NDIS-registered, with referral workflows aligned to the NDIS Practice Standards." },
            { Icon: Clock, title: "< 2 hour response", text: "During business hours, you'll hear back fast. We respect your time and your participant's urgency." },
            { Icon: CheckCircle2, title: "Matched to outcomes", text: "We only take on referrals we can genuinely support — with the right house, staff and funding fit." },
          ].map((i) => (
            <div key={i.title} className="alpha-card p-6">
              <i.Icon className="w-6 h-6 text-[color:var(--alpha-purple-700)]" />
              <h4 className="mt-3 font-display text-lg font-semibold">{i.title}</h4>
              <p className="text-sm text-[color:var(--alpha-mute)] mt-1">{i.text}</p>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
